import React, { ReactElement, useState, useEffect } from 'react';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { Spinner } from 'src/web/components/Spinner/Spinner';
import { Button } from 'src/web/components/Buttons/Button/Button';
import { CoursesFilterData } from 'src/ReduxStorage/InstitutionAdminSettings/types';
import { pNotifyAlert } from 'src/web/components/Pnotify/component';
import { Popover } from '../../../../components/Popover/component';
import { useGetCoursesInformation } from '../../hooks';
import {
  CourseAssignedItem,
  CourseAssignedItemBody,
  CourseImage,
  CourseName,
  CoursePath,
  CoursesAssignedWrapper,
  CoursesSpinnerWrapper,
  LoadMoreButton,
} from './styles';
import {
  PopoverContentBody,
  PopoverContentHeader,
  QuestionCircleIcon,
} from '../../styles';
import { EnrolCoursesModal } from '../../LearnersPage/ViewLearners/EnrolCoursesModal/component';
import { AssignToCoursesModal } from '../../TeamPage/ViewTeamMembers/AssignToCoursesModal/component';
import { AssignedCourseRolesType } from '../../types';

export interface CourseAssignedPopoverProps {
  memberType: 'team-member' | 'learner';
  coursePaths: readonly { coursePath: string }[];
  institutionPath: string;
  allCoursesFilterData: CoursesFilterData;
  currentUserId: string;
  totalCoursesAssigned?: number;
  scheduleAssignToCourse: (params: {
    ids: string[];
    totalAssignedUsers: number;
    courseRoles: AssignedCourseRolesType;
  }) => Promise<unknown>;
}

export const CourseAssignedPopover = ({
  memberType,
  coursePaths,
  institutionPath,
  allCoursesFilterData,
  currentUserId,
  totalCoursesAssigned,
  scheduleAssignToCourse,
}: CourseAssignedPopoverProps): ReactElement => {
  const [pageSize, setPageSize] = useState(0);
  const [showLoadMoreButton, setShowLoadMoreButton] = useState(false);
  const [isOpened, setIsOpened] = useState(false);
  const [
    isShowingAssignToCoursesModal,
    setIsShowingAssignToCoursesModal,
  ] = useState(false);
  const [
    coursesData,
    isLoadingCoursesData,
    loadMoreCourses,
  ] = useGetCoursesInformation(
    institutionPath,
    coursePaths.map((course) => course.coursePath),
    pageSize
  );
  const [isAssigningCourses, setIsAssigningCourses] = useState(false);
  const [errorAssigningCourses, setErrorAssigningCourses] = useState('');

  const totalCourses = totalCoursesAssigned || coursePaths.length;

  const onAssignToCourses = (courseRoles: AssignedCourseRolesType): void => {
    setIsAssigningCourses(true);
    setErrorAssigningCourses('');
    scheduleAssignToCourse({
      ids: [currentUserId],
      courseRoles,
      totalAssignedUsers: 1,
    })
      .then(() => {
        setIsShowingAssignToCoursesModal(false);
        pNotifyAlert({
          title: 'Success',
          text: `Adding one ${
            memberType === 'learner' ? 'learner' : 'member'
          } to courses. You can check the progress on the Recently assigned courses tab`,
          type: 'success',
        });
      })
      .catch((err) => setErrorAssigningCourses(err?.message))
      .finally(() => setIsAssigningCourses(false));
  };

  useEffect(() => {
    // set page size to 100, when popover is opened so it start to fetch initial courses data
    if (isOpened) setPageSize(100);
  }, [isOpened]);

  useEffect(() => {
    // if fetched coursesData is less then total coursesPaths, means there is more courses data to be fetched
    if (coursesData.length < coursePaths.length) {
      setShowLoadMoreButton(true);
    } else {
      setShowLoadMoreButton(false);
    }
  }, [coursesData, coursePaths]);

  return (
    <>
      {isShowingAssignToCoursesModal &&
        (memberType === 'learner' ? (
          <EnrolCoursesModal
            courses={Object.keys(allCoursesFilterData).map(
              (coursePath) => allCoursesFilterData[coursePath]
            )}
            errorMessage={errorAssigningCourses}
            institutionPath={institutionPath}
            isAssigningFromCoursesLoading={isAssigningCourses}
            onAssignToCourses={onAssignToCourses}
            onHide={() => setIsShowingAssignToCoursesModal(false)}
            totalSelectedMembers={1}
          />
        ) : (
          <AssignToCoursesModal
            courses={Object.keys(allCoursesFilterData).map(
              (coursePath) => allCoursesFilterData[coursePath]
            )}
            errorMessage={errorAssigningCourses}
            institutionPath={institutionPath}
            isAssigningFromCoursesLoading={isAssigningCourses}
            onAssignToCourses={onAssignToCourses}
            onHide={() => setIsShowingAssignToCoursesModal(false)}
            totalSelectedMembers={1}
          />
        ))}
      {totalCourses === 0 ? (
        <Button
          colour="kelp"
          label="Add to courses"
          onClick={() => {
            setIsShowingAssignToCoursesModal(true);
          }}
        />
      ) : (
        <Popover
          onOpen={() => setIsOpened(true)}
          toggle={
            <span>
              {totalCourses} <QuestionCircleIcon icon={faQuestionCircle} />
            </span>
          }
          popOverInitialState={{
            unstable_preventOverflow: false,
            unstable_fixed: true,
          }}
          placement="bottom"
          content={
            <div>
              <PopoverContentHeader>Courses assigned</PopoverContentHeader>
              <PopoverContentBody>
                <CoursesAssignedWrapper>
                  {coursesData.map(({ name, path, promoImage }) => (
                    <CourseAssignedItem>
                      <CourseImage
                        src={promoImage}
                        alt="course-assigned-tile"
                      />
                      <CourseAssignedItemBody>
                        <CourseName>{name}</CourseName>
                        <CoursePath>{path}</CoursePath>
                      </CourseAssignedItemBody>
                    </CourseAssignedItem>
                  ))}
                </CoursesAssignedWrapper>
                {isLoadingCoursesData && (
                  <CoursesSpinnerWrapper>
                    <Spinner />
                  </CoursesSpinnerWrapper>
                )}
                {showLoadMoreButton && (
                  <div>
                    <LoadMoreButton onClick={() => loadMoreCourses()}>
                      Load more
                    </LoadMoreButton>
                  </div>
                )}
              </PopoverContentBody>
            </div>
          }
          ariaLabel="Courses assigned"
        />
      )}
    </>
  );
};
